import * as React from 'react';

import { Box, Stack, Typography, Chip, Tooltip } from '@mui/material';

interface RejectionCode {
    id: number;
    code: string;
    libelle: string;
}

interface RejectionCodesChipsProps {
    codes: RejectionCode[];
    type: 'eligible' | 'ineligible';
    title?: string;
}

/**
 * Composant pour afficher une liste de codes de rejet sous forme de chips
 *
 * Affiche les codes de rejet d'une facture sous forme de petits chips cliquables
 * avec tooltip. Permet de distinguer visuellement les codes éligibles des non-éligibles.
 * Utilisé dans les accordéons de détail d'éligibilité.
 *
 * @param codes - Tableau des codes de rejet à afficher
 * @param type - Type de codes : 'eligible' (verts) ou 'ineligible' (gris)
 * @param title - Titre optionnel à afficher au-dessus des chips (ex: "Éligibles (2)")
 */
const RejectionCodesChips: React.FunctionComponent<RejectionCodesChipsProps> = function ({
    codes,
    type,
    title,
}) {
    if (!codes || !Array.isArray(codes) || codes.length === 0) {
        return null;
    }

    const isEligible = type === 'eligible';

    return (
        <Box sx={{ mb: 1 }}>
            {title && (
                <Typography
                    variant="caption"
                    color={isEligible ? 'success.main' : 'text.secondary'}
                    fontWeight="bold"
                    sx={{ display: 'block', mb: 0.5 }}
                >
                    {title} ({codes.length}) :
                </Typography>
            )}
            <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
            >
                {codes.map((code) => (
                    <Tooltip
                        key={code.id}
                        title={`${code.code}: ${code.libelle}`}
                    >
                        <Chip
                            label={code.code}
                            size="small"
                            variant="outlined"
                            color={isEligible ? 'success' : 'default'}
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                '& .MuiChip-label': {
                                    px: 0.5,
                                },
                            }}
                        />
                    </Tooltip>
                ))}
            </Stack>
        </Box>
    );
};

export default RejectionCodesChips;
